import { useMemo } from 'react';
import type { Lote, QrDoLote } from '../../api/lotes.js';
import type { TamanhoDeEtiqueta } from './etiqueta.js';
import { caminhoDaMatriz, layoutDaEtiqueta, MARGEM_EM_MODULOS } from './etiqueta.js';
import { layoutDoTextoDoLote, linhasDeDetalhe } from './etiqueta-lote.js';
import './DesenhoDaEtiqueta.css';

type Props = {
  readonly tamanho: TamanhoDeEtiqueta;
  readonly qr: QrDoLote;
  readonly lote: Pick<Lote, 'codPro' | 'codDer' | 'datVlt'>;
  /** Na folha de impressão, largura e altura em milímetros de verdade. */
  readonly emMilimetros?: boolean;
};

/**
 * A etiqueta de lote: o QR na mesma posição da etiqueta de produto, o
 * `codLot` em letra grande abaixo da margem branca e, menores, o produto, a
 * derivação e a validade.
 *
 * O `codLot` escrito é o que o middleware devolveu na matriz — igual ao
 * pedido por contrato (`obterQrDoLote` recusa o contrário).
 */
export function DesenhoDaEtiquetaDeLote({ tamanho, qr, lote, emMilimetros = false }: Props) {
  const layout = useMemo(() => layoutDaEtiqueta(tamanho), [tamanho]);
  const caminho = useMemo(() => caminhoDaMatriz(qr.modulos, qr.n), [qr.modulos, qr.n]);
  const detalhes = linhasDeDetalhe(lote);
  const texto = layoutDoTextoDoLote(tamanho, qr.codLot, detalhes);

  const origemX = layout.simbolo.xMm + MARGEM_EM_MODULOS * layout.moduloMm;
  const origemY = layout.simbolo.yMm + MARGEM_EM_MODULOS * layout.moduloMm;

  return (
    <svg
      className={emMilimetros ? 'etiqueta' : 'etiqueta etiqueta--previa'}
      role="img"
      aria-label={`Etiqueta do lote ${qr.codLot}`}
      viewBox={`0 0 ${String(layout.larguraMm)} ${String(layout.alturaMm)}`}
      {...(emMilimetros
        ? { width: `${String(layout.larguraMm)}mm`, height: `${String(layout.alturaMm)}mm` }
        : {})}
      shapeRendering="crispEdges"
    >
      <rect className="etiqueta__papel" width={layout.larguraMm} height={layout.alturaMm} />
      <path
        className="etiqueta__tinta"
        d={caminho}
        transform={`translate(${String(origemX)} ${String(origemY)}) scale(${String(layout.moduloMm)})`}
      />
      <text
        className="etiqueta__codigo"
        x={texto.centroMm}
        y={texto.lote.linhaDeBaseMm}
        fontSize={texto.lote.fonteMm}
        textAnchor="middle"
      >
        {qr.codLot}
      </text>
      {detalhes.map((linha, indice) => (
        <text
          key={linha}
          className="etiqueta__detalhe"
          x={texto.centroMm}
          y={texto.detalhe.linhasDeBaseMm[indice]}
          fontSize={texto.detalhe.fonteMm}
          textAnchor="middle"
        >
          {linha}
        </text>
      ))}
    </svg>
  );
}
